/**
 * #690: the enterprise-wide Memory read path's client, kept as its own module rather than folded into the
 * large `@/api` so this new surface can be added without touching it. `MemoryView` mocks this module
 * wholesale in tests, the same way sibling views mock `@/api`.
 */
import type {
    MemoryConstellationResponse,
    MemoryCorrectionState,
    MemoryGraphEdge,
    MemoryGraphQuery,
    MemoryKnowledgeType,
    MemoryLabel,
    MemoryNodeOrigin,
    MemoryRecallHealth,
} from '@zerohuman/shared'

export type MemoryGraphNode = {
    id: string
    body: string
    layer: string
    layerId: string
    knowledgeType: MemoryKnowledgeType
    correctionState: MemoryCorrectionState
    createdAt: string
    provenance: string | null
    authorId: string | null
    labels: MemoryLabel[]
    /** Whether the signed-in caller holds the grant to correct or exclude this node. */
    correctable: boolean
    /** Whether the OS trained this node or a run retained it (#959). Absent reads as trained. */
    origin?: MemoryNodeOrigin
}

/**
 * A bounded page of the graph: `truncated` is true whenever there is more than what `nodes` carries — because
 * `total` exceeds the page, or because a derived filter stopped at the server's scan ceiling and so counted only
 * the rows it read.
 */
export type MemoryGraphResponse = {
    nodes: MemoryGraphNode[]
    edges: MemoryGraphEdge[]
    total: number
    truncated: boolean
    /**
     * The labels this page may filter by, from the enterprise's teams rather than from the nodes above — a
     * bounded page carries the newest entries only, so a team whose knowledge is all older is still selectable.
     * Absent where the server had nothing readable to offer.
     */
    labels?: MemoryLabel[]
}

/**
 * Every filter the caller named, by its own key, so a filter added to `MemoryGraphQuery` reaches the API without
 * an edit here. A filter that narrows nothing is left off: an empty string, and a switch that is off
 * (`includeRunLearned: false`), which the API reads as off when it is absent anyway.
 */
function toQueryString(query: MemoryGraphQuery): string {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== '' && value !== false) params.set(key, String(value))
    }
    const search = params.toString()
    return search ? `?${search}` : ''
}

/**
 * A read of the memory graph. A refused or broken read is raised, never returned: a `SessionGuard` 401 or a 5xx
 * answers a body shaped like `{ statusCode, message }`, and parsing that as a graph would hand the page an
 * object with no `nodes` to draw and no failure to report. Raising it is what lets `MemoryView` say "Memory
 * could not be read just now" instead of throwing on `undefined` at the next reactive read.
 */
async function requestJson<T>(path: string): Promise<T> {
    const response = await fetch(`/v1${path}`, { credentials: 'include' })
    if (!response.ok) {
        throw new Error(`memory_request_failed:${response.status}`)
    }
    return (await response.json()) as T
}

export const memoryGraphApi = {
    graph: (query: MemoryGraphQuery = {}): Promise<MemoryGraphResponse> =>
        requestJson<MemoryGraphResponse>(`/memory${toQueryString(query)}`),
    /**
     * The same memory as its store holds it: units and the links between them, under the same filters. The answer
     * says for itself when the store could not be read (`status: 'unavailable'`), so that is not a failed request.
     */
    constellation: (query: MemoryGraphQuery = {}): Promise<MemoryConstellationResponse> =>
        requestJson<MemoryConstellationResponse>(`/memory/constellation${toQueryString(query)}`),
    node: (id: string): Promise<MemoryGraphNode | null> => requestJson<MemoryGraphNode | null>(`/memory/${id}`),
    /** How often memory could be read when a run or a chat reply asked it, over the last day. */
    recallHealth: (): Promise<MemoryRecallHealth> => requestJson<MemoryRecallHealth>('/memory/recall-health'),
    /**
     * Exclude one entry from future answers — the tombstone the store already keeps (`POST /memory/:id/tombstone`),
     * which stops Ask drawing on it without destroying the record that it was retained. Authorised server-side;
     * the page only offers it for a node the read path already marked `correctable`.
     */
    exclude: async (id: string): Promise<void> => {
        const response = await fetch(`/v1/memory/${id}/tombstone`, { method: 'POST', credentials: 'include' })
        if (!response.ok) {
            throw new Error(`memory_exclude_failed:${response.status}`)
        }
    },
}
